import * as React from "react";
import * as _ from "underscore";
import * as qs from "qs";
import { AnyAction, Dispatch } from "redux";
import { connect } from "react-redux";

import { formatCurrency } from "@eave/shared/utils/currency";
import {
  Table,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableExpandHeader,
  TableRow,
  TableExpandRow,
  TableExpandedRow,
  TextInput,
  ComboBox,
  FormGroup,
  Tooltip,
} from "carbon-components-react";
import { Edit20, Renew16, Locked24, Unlocked24, ResetAlt24 } from "@carbon/icons-react";
import {
  AppShape,
  DecisionCriteriaDisplayValues,
  LoanPrograms,
  loanTypeToShortName,
  activeDecisionCriteriaWithBackwardsSupport,
  PropertyTypes,
  operatorHumanizeLoanProgramName,
} from "@eave/shared/types/api";
import { featureFlag } from "config";
import { formatPercent } from "@eave/shared/utils/percent";

import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import APIClient from "web_modules/APIClient";
import Money from "web_modules/Money";
import { deepInstanceToWsList } from "underwriter_dashboard/shared/WorksheetEditor/reducer";
import { WS_NAME } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";
import { getWsByName } from "underwriter_dashboard/shared/WorksheetEditor/wsHelpers";
import { IWorksheetState } from "underwriter_dashboard/shared/WorksheetEditor/interfaces";
import TooltipForm from "./TooltipForm";
import { loadApp } from "../../actions";
import { moneyInputToNumber } from "web_modules/shared/FormHelpers/carbon-helpers";

const styles = require("../styles.module.scss");

const emptyValue = <span className={styles.emptyValue}>—</span>;

// TODO: jjw: uncomment (and delete block below) when feature flags no longer used

// const loanProgramsForDecisionCriteria = (dc: string) => {
//   const keys = [
//     LoanPrograms.THIRTY_YEAR,
//     LoanPrograms.FIVE_SIX_THIRTY_DAY_SOFR_ARM,
//     LoanPrograms.SEVEN_SIX_THIRTY_DAY_SOFR_ARM,
//     LoanPrograms.TEN_SIX_THIRTY_DAY_SOFR_ARM,
//   ];
//   if (dc.toLowerCase().includes("jumbo")) {
//     return keys;
//   }
//   return keys.filter(k => k !== LoanPrograms.TEN_SIX_THIRTY_DAY_SOFR_ARM); // jumbo-only option
// };

const loanProgramsForDecisionCriteria = (dc: string) => {
  const isJumbo = dc.toLowerCase().includes("jumbo");
  const sofrJumbo = featureFlag("sofrJumbo");
  const sofrConforming = featureFlag("sofrConforming");

  if (isJumbo && sofrJumbo) {
    return [
      LoanPrograms.THIRTY_YEAR,
      LoanPrograms.FIVE_SIX_THIRTY_DAY_SOFR_ARM,
      LoanPrograms.SEVEN_SIX_THIRTY_DAY_SOFR_ARM,
      LoanPrograms.TEN_SIX_THIRTY_DAY_SOFR_ARM,
    ];
  }
  if (isJumbo && !sofrJumbo) {
    return [
      LoanPrograms.THIRTY_YEAR,
      LoanPrograms.FIVE_ARM,
      LoanPrograms.SEVEN_ARM,
      LoanPrograms.TEN_ARM,
    ];
  }
  if (!isJumbo && sofrConforming) {
    return [
      LoanPrograms.THIRTY_YEAR,
      LoanPrograms.FIVE_SIX_THIRTY_DAY_SOFR_ARM,
      LoanPrograms.SEVEN_SIX_THIRTY_DAY_SOFR_ARM,
    ];
  }
  if (!isJumbo && !sofrConforming) {
    return [LoanPrograms.THIRTY_YEAR, LoanPrograms.FIVE_ARM, LoanPrograms.SEVEN_ARM];
  }

  throw new Error("not reached");
};

// NOTE: jjw: these are local because they're only used as keys in: this.state.calcs[<key>]
// (a little weird, but a first step toward making legacy state management clearer here...)
const loanProgramKey = {
  [LoanPrograms.FIVE_ARM]: "FIVE_ARM",
  [LoanPrograms.SEVEN_ARM]: "SEVEN_ARM",
  [LoanPrograms.TEN_ARM]: "TEN_ARM",
  [LoanPrograms.THIRTY_YEAR]: "THIRTY_YEAR",
  [LoanPrograms.FIVE_SIX_THIRTY_DAY_SOFR_ARM]: "FIVE_SIX_THIRTY_DAY_SOFR_ARM",
  [LoanPrograms.SEVEN_SIX_THIRTY_DAY_SOFR_ARM]: "SEVEN_SIX_THIRTY_DAY_SOFR_ARM",
  [LoanPrograms.TEN_SIX_THIRTY_DAY_SOFR_ARM]: "TEN_SIX_THIRTY_DAY_SOFR_ARM",
};

interface HurdleEditorProps extends HurdleProps {
  dispatch?: Dispatch<AnyAction>;
}

interface LoanProductHurdleEditorState {
  purchase_price: string;
  loan_amount_base: string;
  down_payment: string;
  down_payment_percent?: string;
  decision_criteria: string;
  calcs: object;
  overrides: object;
  expandedRows: {
    propertyTax: boolean;
    insurance: boolean;
    hoa: boolean;
    floodInsurance: boolean;
    earthquakeInsurance: boolean;
    windAndHailInsurance: boolean;
    mortgageInsurance: boolean;
    decision_criteria: boolean;
    qualifyingMonthlyPitia: boolean;
  };
  worksheetOverrides: {
    decision_criteria: string;
    operator_closing_cost_estimated_percent: number;
    operator_max_dti: number;
    operator_max_loan_amount: number;
    operator_max_purchase_price: number;
    operator_max_ltv: number;
    operator_minimum_loan_amount: number;
    reserves_for_max_affordability: number;
  };
  worksheet: IWorksheetState;
}

export const conformingOrHighBalance = (app: AppShape): string => {
  if (app.decision.decisioning_criteria_name.includes("Jumbo")) {
    return "";
  }

  const propertyType = app.operator_subject_property_type || app.property_type;

  switch (propertyType) {
    case PropertyTypes.SFR:
    case PropertyTypes.SFRATT:
    case PropertyTypes.PUD:
    case PropertyTypes.PUDATT:
    case PropertyTypes.SiteCondo:
    case PropertyTypes.LowRiseCondo:
    case PropertyTypes.MidRiseCondo:
    case PropertyTypes.HighRiseCondo:
      return app.decision.max_conforming_loan_amount === app.decision.conforming_loan_limit_1_unit
        ? "(Conforming)"
        : "(High Balance)";
    case PropertyTypes.Duplex:
    case PropertyTypes.DuplexATT:
      return app.decision.max_conforming_loan_amount === app.decision.conforming_loan_limit_2_unit
        ? "(Conforming)"
        : "(High Balance)";
    case PropertyTypes.Triplex:
    case PropertyTypes.TriplexATT:
      return app.decision.max_conforming_loan_amount === app.decision.conforming_loan_limit_3_unit
        ? "(Conforming)"
        : "(High Balance)";
    case PropertyTypes.Quadplex:
    case PropertyTypes.QuadplexATT:
      return app.decision.max_conforming_loan_amount === app.decision.conforming_loan_limit_4_unit
        ? "(Conforming)"
        : "(High Balance)";
    default:
      return "";
  }
};

const decisionCriteriaOptions = (currentDC?: string) => {
  return activeDecisionCriteriaWithBackwardsSupport(currentDC).map(dc => ({
    value: dc,
    label: DecisionCriteriaDisplayValues[dc],
  }));
};

class LoanProductHurdleEditor extends React.Component<
  HurdleEditorProps,
  LoanProductHurdleEditorState
> {
  constructor(props) {
    super(props);

    const purchase_price = Number(props.app.computed_properties.purchase_price);
    const loan_amount_base = Number(props.app.computed_properties.loan_amount_base);

    this.state = {
      purchase_price: String(purchase_price),
      loan_amount_base: String(loan_amount_base),
      down_payment: String(purchase_price - loan_amount_base),
      decision_criteria: props.app.decision.decisioning_criteria_name,
      calcs: {},
      overrides: {
        propertyTax: null,
        insurance: null,
        hoa: null,
        floodInsurance: null,
        earthquakeInsurance: null,
        windAndHailInsurance: null,
        mortgageInsurance: null,
      },
      expandedRows: {
        propertyTax: false,
        insurance: false,
        hoa: false,
        floodInsurance: false,
        earthquakeInsurance: false,
        windAndHailInsurance: false,
        mortgageInsurance: false,
        decision_criteria: false,
        qualifyingMonthlyPitia: false,
      },
      worksheetOverrides: {
        decision_criteria: "",
        operator_closing_cost_estimated_percent: undefined,
        operator_max_dti: undefined,
        operator_max_loan_amount: undefined,
        operator_max_purchase_price: undefined,
        operator_max_ltv: undefined,
        operator_minimum_loan_amount: undefined,
        reserves_for_max_affordability: undefined,
      },
      worksheet: null,
    };

    this.calculate = _.debounce(this.calculate, 400);

    this.fetchWorksheets();
  }

  public componentDidMount() {
    this.calculate();
  }

  public handleComboBoxChange = (fieldName, x) => {
    if (x.selectedItem) {
      const update: Partial<LoanProductHurdleEditorState> = {
        [fieldName]: x.selectedItem.value,
      };
      this.setState(update as LoanProductHurdleEditorState, this.calculate);
    }
  };

  public applicableProducts() {
    const { decision_criteria } = this.state;
    return loanProgramsForDecisionCriteria(decision_criteria);
  }

  public handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    this.setState(prevState => {
      return {
        ...prevState,
        overrides: {
          ...prevState.overrides,
          [name]: value,
        },
      };
    });
  }

  public handleOperatorOverrideChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;

    this.setState(prevState => {
      return {
        worksheetOverrides: {
          ...prevState.worksheetOverrides,
          [name]: value,
        },
      };
    });
  }

  public handleSandboxCalculatorChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    const { purchase_price, down_payment_percent, down_payment } = this.state;

    const purchasePrice = Number(purchase_price);
    const downpaymentPercent = Number(down_payment_percent);
    const downpayment = Number(down_payment);
    const update: Partial<LoanProductHurdleEditorState> = {
      [name]: value,
    };
    if (name === "loan_amount_base") {
      update.down_payment = String(purchasePrice - Number(value));
      update.down_payment_percent = String(100 * (1 - Number(value) / purchasePrice));
    }
    if (name === "down_payment") {
      update.loan_amount_base = String(purchasePrice - Number(value));
      update.down_payment_percent = String(100 * (Number(value) / purchasePrice));
    }
    if (name === "down_payment_percent" && parseFloat(value)) {
      const downPaymentRatio = Number(value) / 100;
      if (downPaymentRatio > 1 || downPaymentRatio < 0) {
        return;
      }
      update.down_payment = String(purchasePrice * downPaymentRatio);
      update.loan_amount_base = String(purchasePrice * (1 - downPaymentRatio));
    }
    if (name === "purchase_price") {
      if (down_payment_percent) {
        const downPaymentRatio = downpaymentPercent / 100;
        update.down_payment = String(downPaymentRatio * Number(value));
        update.loan_amount_base = String((1 - downPaymentRatio) * Number(value));
      } else {
        const existingRatio = downpayment / purchasePrice;
        update.down_payment = String(Number(value) * existingRatio);
        update.down_payment_percent = (existingRatio * 100).toFixed(2);
        update.loan_amount_base = String(Number(value) * (1 - existingRatio));
      }
    }
    this.setState(update as LoanProductHurdleEditorState, this.calculate);
  }

  public calculateParams(program) {
    const { down_payment, purchase_price, decision_criteria } = this.state;
    const {
      app: { id },
    } = this.props;
    return {
      down_payment,
      finance_closing_costs: false,
      is_research: true,
      loan_application_id: id,
      loan_program: program,
      purchase_price,
      decision_criteria,
    };
  }

  public calculate() {
    this.applicableProducts().forEach(loanProduct => {
      APIClient.get(
        `/uw/product_calculator?${qs.stringify(this.calculateParams(loanProduct))}`,
      ).then((resp: any) => {
        const monthlyPayment = parseFloat(resp.calculation.monthly_payment);
        const qualifyingMonthlyPayment = parseFloat(resp.calculation.qualifying_monthly_payment);
        const maxQualifyingMonthlyPayment = parseFloat(
          resp.calculation.max_qualifying_monthly_payment,
        );
        const monthlyTIA = parseFloat(resp.calculation.monthly_tia);
        const calculations = {
          apr: parseFloat(resp.calculation.apr).toFixed(3),
          combined_monthly_payment: monthlyPayment + monthlyTIA,
          loan_amount_total: parseFloat(resp.calculation.loan_amount_total),
          loan_amount_base: parseFloat(resp.calculation.loan_amount_base),
          loan_program: loanProduct,
          max_loan_amount: parseFloat(resp.calculation.max_loan_amount),
          max_loan_amount_original: parseFloat(resp.calculation.max_loan_amount_original),
          max_purchase_price: parseFloat(resp.calculation.max_purchase_price),
          max_purchase_price_original: parseFloat(resp.calculation.max_purchase_price_original),
          max_purchase_price_at_max_loan_amount: parseFloat(resp.calculation.max_purchase_price),
          max_purchase_price_ltv_calculated: resp.calculation.max_purchase_price_ltv_calculated,
          max_purchase_price_ltv_override: resp.calculation.max_purchase_price_ltv_override,
          max_ltv_for_input_purchase_price: parseFloat(resp.calculation.max_ltv),
          down_payment_at_max:
            parseFloat(resp.calculation.max_purchase_price) -
            parseFloat(resp.calculation.max_loan_amount),
          ltv_at_max:
            (100 * parseFloat(resp.calculation.max_loan_amount)) /
            parseFloat(resp.calculation.max_purchase_price),
          monthly_payment: monthlyPayment.toFixed(2),
          qualifying_monthly_payment: qualifyingMonthlyPayment.toFixed(2),
          qualifying_combined_monthly_payment: qualifyingMonthlyPayment + monthlyTIA,
          max_qualifying_combined_monthly_payment: maxQualifyingMonthlyPayment + monthlyTIA,
          max_monthly_payment: maxQualifyingMonthlyPayment,
          max_dti_override: resp.calculation.max_dti_override,
          max_dti_calculated: resp.calculation.max_dti_calculated,
          max_purchase_price_reserves_override:
            resp.calculation.max_purchase_price_reserves_override,
          max_purchase_price_reserves_calculated:
            resp.calculation.max_purchase_price_reserves_calculated,
          monthly_tia: monthlyTIA.toFixed(2),
          rate: parseFloat(resp.calculation.rate).toFixed(3),
          qualifying_rate: parseFloat(resp.calculation.qualifying_rate).toFixed(3),
          input_purchase_price: parseFloat(resp.calculation.input.purchase_price).toFixed(2),
          input_down_payment: parseFloat(resp.calculation.input.down_payment).toFixed(2),

          max_bucket_ltv: resp.calculation.max_bucket_ltv,
          max_cash_close_purchase_price: parseFloat(resp.calculation.max_cash_close_purchase_price),
          max_cash_close_loan_amount: parseFloat(resp.calculation.max_cash_close_loan_amount),
          min_appraisal_for_max_cc_loan_amount: parseFloat(
            resp.calculation.min_appraisal_for_max_cc_loan_amount,
          ),
          min_appraisal_for_input_cc_purchase_price: parseFloat(
            resp.calculation.min_appraisal_for_input_cc_purchase_price,
          ),
          input_cc_purchase_price: parseFloat(resp.calculation.input_cc_purchase_price),
        };

        this.setState(prevState => ({
          ...prevState,
          calcs: {
            ...prevState.calcs,
            [loanProgramKey[loanProduct]]: calculations,
          },
        }));
      });
    });
  }

  public renderSummary() {
    const { app, dispatch } = this.props;
    const product_selected = app.product_selection_time !== null;
    const { expandedRows, overrides, worksheetOverrides, worksheet } = this.state;

    const activeDCs = decisionCriteriaOptions(worksheetOverrides.decision_criteria);

    const currentDC = activeDCs.filter(
      item => item.value === app.decision.decisioning_criteria_name,
    );

    const primaryProduct = {
      appraisedValue: app.appraisal ? Number(app.appraisal.appraised_value) : 0,
      ltv: Number(app.computed_properties.ltv),
      avmValue: app.purchased_property_avm || 0,
      closingCosts: app.selected_closing_costs_financing ? Number(app.closing_fees_total) : 0,
      downpayment: Number(app.computed_properties.downpayment),
      effectiveRate: Number(app.decision.effective_rate),
      qualificationRate: Number(app.decision.qualification_rate),
      loanAmountBase: Number(app.computed_properties.loan_amount_base),
      vaFundingFeePercent: 100 * Number(app.computed_properties.va_funding_fee_percent),
      vaFundingFeeDollars: Number(app.computed_properties.va_funding_fee_dollars),
      loanAmountTotal: Number(app.computed_properties.loan_amount_total),
      loanProgram: app.selected_loan_program,
      loanType: loanTypeToShortName(app.computed_properties.loan_type_name),
      monthlyDebt: Number(app.decision.monthly_debt),
      monthlyPayment: Number(app.decision.monthly_payment),
      effectiveAPR: Number(app.decision.effective_apr),

      propertyTax: app.decision.monthly_property_taxes,
      insurance: app.decision.monthly_home_owners_insurance,
      hoa: app.decision.monthly_home_owners_association,
      floodInsurance: app.decision.monthly_flood_insurance,
      earthquakeInsurance: app.decision.monthly_earthquake_insurance,
      windAndHailInsurance: app.decision.monthly_wind_and_hail_insurance,
      mortgageInsurance: app.decision.monthly_mortgage_insurance,

      purchasePrice: Number(app.computed_properties.purchase_price),
      totalPitia: Number(app.decision.total_housing_expenses),
      qualifyingTotalPAndI: Number(app.decision.qualifying_monthly_payment),
      qualifyingTotalPitia:
        Number(app.decision.qualifying_monthly_payment) + Number(app.decision.monthly_tia),
      qualifyingMonthlyDebt: Number(app.decision.qualifying_monthly_debt),

      dti: Number(app.decision.dti),
      county: app.computed_properties.purchased_property_county || app.property_county,
      conformingLimit: app.decision.max_conforming_loan_amount,
      conformingOrHighBalance: conformingOrHighBalance(app),
      decisionCriteriaName: app.decision.decisioning_criteria_name,
      decisionCriteriaFriendlyName: currentDC.length > 0 ? currentDC[0].label : "",
      maxPurchasePrice: Number(app.decision.max_purchase_price),
      maxLoanAmount: Number(app.decision.max_loan_amount),
      cashOfferMaxPurchasePrice: Number(app.decision.max_cash_offer_purchase_price),
      cashOfferMaxLoanAmount: Number(app.decision.max_cash_offer_loan_amount),

      // FIXME: these fields exist on loan app but are not yet populated
      otherMonthlies: 0,
    };
    const isRateLocked = app.active_rate !== null && app.active_rate.locked;

    const overrideValueIsDifferent = (fieldName: string) => {
      for (let i = 0; i < primaryProduct[fieldName].length; i++) {
        if (primaryProduct[fieldName][i].source === "Override") {
          return primaryProduct[fieldName][i].amount !== overrides[fieldName];
        }
      }
      return true;
    };

    const monthlyPaymentRows = (label: string, fieldName: string) => {
      return (
        <>
          <TableExpandRow
            onExpand={() => {
              this.setState(prevState => {
                return {
                  ...prevState,
                  expandedRows: {
                    ...prevState.expandedRows,
                    [fieldName]: !prevState.expandedRows[fieldName],
                  },
                };
              });
            }}
            isExpanded={expandedRows[fieldName]}
            ariaLabel="Expand row to see hierarchy and override"
          >
            <TableCell>{label}</TableCell>
            <TableCell>
              {primaryProduct[fieldName] ? primaryProduct[fieldName][0].source : ""}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              <Money
                decimals
                value={primaryProduct[fieldName] ? primaryProduct[fieldName][0].amount : 0}
              />
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>{emptyValue}</TableCell>
          </TableExpandRow>
          {expandedRows[fieldName] && (
            <TableExpandedRow colSpan={5}>
              <div>
                <Table>
                  {(primaryProduct[fieldName] || []).map(item => {
                    return (
                      <TableRow key={item.source}>
                        <TableCell>{item.source}</TableCell>
                        <TableCell>
                          <Money decimals value={item.amount} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell style={{ width: "350px", fontWeight: "bold" }}>
                      New override
                    </TableCell>
                    <TableCell style={{ display: "flex" }}>
                      <TextInput
                        id={fieldName}
                        name={fieldName}
                        value={formatCurrency(overrides[fieldName], 0, 2)}
                        type="text"
                        onChange={e => this.handleOnChange(moneyInputToNumber(e))}
                        onBlur={e => this.handleOnChange(moneyInputToNumber(e))}
                      />
                      <Button
                        disabled={!overrideValueIsDifferent(fieldName)}
                        size="field"
                        onClick={() => {
                          APIClient.put(
                            `/loan_applications/${app.id}`,
                            { [fieldName]: overrides[fieldName] },
                            () => {
                              loadApp(dispatch, app.id);
                            },
                          );
                        }}
                      >
                        Save
                      </Button>
                    </TableCell>
                  </TableRow>
                </Table>
              </div>
            </TableExpandedRow>
          )}
        </>
      );
    };

    const productReselectionButton = (
      <>
        <Button
          disabled={!product_selected}
          size="small"
          renderIcon={ResetAlt24}
          iconDescription="Allow product reselection"
          hasIconOnly
          onClick={() => {
            if (
              window.confirm(
                "Are you sure you want to allow product reselection? If rates are locked they will be expired.",
              )
            ) {
              APIClient.post(`/uw/${app.id}/reset_product_selected`, {}, () => {
                loadApp(dispatch, app.id);
              });
            }
          }}
        />
        <Tooltip direction="right" tabIndex={0}>
          {!product_selected && (
            <p>
              Allow product reselection button disabled because product not selected yet or has
              already been unlocked.
            </p>
          )}
          {product_selected && (
            <p>
              Allows the base loan amount and/or loan program to be updated via the borrower flow.
            </p>
          )}
        </Tooltip>
      </>
    );

    return (
      <div>
        <div>
          <div className={styles.loanProductSummaryContainer}>
            <Table className={styles.loanProductSummary} size="compact">
              <TableHead>
                <TableRow>
                  <TableHeader>Loan Details</TableHeader>
                  <TableHeader />
                  <TableHeader />
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Loan type</TableCell>
                  <TableCell>{primaryProduct.loanType}</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Loan purpose</TableCell>
                  <TableCell>{app.loan_purpose === "" ? "Purchase" : app.loan_purpose}</TableCell>
                  <TableCell>
                    <Tooltip direction="right" tabIndex={0} renderIcon={Edit20}>
                      <ComboBox
                        titleText="Loan purpose"
                        id="loan_purpose"
                        name="loan_purpose"
                        placeholder="loan purpose"
                        selectedItem={app.loan_purpose === "" ? "Purchase" : app.loan_purpose}
                        type="default"
                        onChange={e => {
                          return APIClient.put(`/loan_applications/${app.id}`, {
                            loan_purpose: e.selectedItem.value,
                          })
                            .then(() => {
                              loadApp(dispatch, app.id);
                            })
                            .catch(err => {
                              alert(JSON.stringify(err));
                            });
                        }}
                        size="sm"
                        items={[
                          { label: "Purchase", value: "Purchase" },
                          { label: "Bridge", value: "Bridge" },
                        ]}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Decision criteria</TableCell>
                  <TableCell>{primaryProduct.decisionCriteriaFriendlyName}</TableCell>
                  <TableCell>
                    <Tooltip direction="right" tabIndex={0} renderIcon={Edit20}>
                      <ComboBox
                        titleText="Decision criteria"
                        id="decision_criteria"
                        name="decision_criteria"
                        placeholder="decision criteria"
                        selectedItem={activeDCs.find(
                          x => x.value === worksheetOverrides.decision_criteria,
                        )}
                        type="default"
                        onChange={e => {
                          if (isRateLocked) {
                            alert(
                              "Decision criteria can't be changed when Rate is locked. Please allow product re-selection first, change the decision criteria, and then re-lock the rate.",
                            );
                          } else {
                            APIClient.post(`/worksheets/${worksheet.id}`, {
                              application_id: app.id,
                              updates: [
                                {
                                  name: "decision_criteria",
                                  value: e.selectedItem.value,
                                },
                              ],
                            })
                              .then(() => {
                                loadApp(dispatch, app.id);
                              })
                              .catch(err => {
                                alert(JSON.stringify(err));
                              });
                          }
                        }}
                        size="sm"
                        items={activeDCs}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>
                    <span style={{ verticalAlign: "middle" }}>
                      {operatorHumanizeLoanProgramName[primaryProduct.loanProgram]}{" "}
                    </span>
                  </TableCell>
                  <TableCell>{productReselectionButton}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Term</TableCell>
                  <TableCell>30 years</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Rate</TableCell>
                  <TableCell>
                    {primaryProduct.effectiveRate.toFixed(3)}%{" "}
                    {isRateLocked ? (
                      <Locked24 style={{ verticalAlign: "bottom" }} />
                    ) : (
                      <Unlocked24 style={{ verticalAlign: "bottom" }} />
                    )}
                    {isRateLocked ? "locked" : "unlocked"}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Qualification Rate</TableCell>
                  <TableCell>
                    {primaryProduct.qualificationRate.toFixed(3)}%{" "}
                    {app.decision.rate.loan_program.indexOf("ARM") >= 0 && (
                      <Tooltip direction="right" tabIndex={0} triggerText="">
                        <p>index: {formatPercent(app.decision.rate.index)}</p>
                        <p>margin: {formatPercent(app.decision.rate.margin)}</p>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>APR</TableCell>
                  <TableCell>{primaryProduct.effectiveAPR.toFixed(3)}%</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Base loan amount</TableCell>
                  <TableCell>
                    <Money decimals value={primaryProduct.loanAmountBase} />
                  </TableCell>
                  <TableCell>{productReselectionButton}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>VA funding fee</TableCell>
                  <TableCell>
                    {primaryProduct.vaFundingFeePercent > 0 ? (
                      <>
                        <Money decimals value={primaryProduct.vaFundingFeeDollars} />
                        &nbsp;({formatPercent(primaryProduct.vaFundingFeePercent, 2)})
                      </>
                    ) : (
                      emptyValue
                    )}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Total loan amount</TableCell>
                  <TableCell>
                    <Money decimals value={primaryProduct.loanAmountTotal} />
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Purchase price</TableCell>
                  <TableCell>
                    <Money decimals value={primaryProduct.purchasePrice} />
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>LTV</TableCell>
                  <TableCell>{formatPercent(primaryProduct.ltv * 100, 2)}</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Downpayment</TableCell>
                  <TableCell>
                    <Money decimals value={primaryProduct.downpayment} />
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Closing costs</TableCell>
                  <TableCell>
                    <Money decimals value={primaryProduct.closingCosts} />
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Appraised value</TableCell>
                  <TableCell>
                    {primaryProduct.appraisedValue > 0 ? (
                      <Money decimals value={primaryProduct.appraisedValue} />
                    ) : (
                      emptyValue
                    )}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>AVM estimated value</TableCell>
                  <TableCell>
                    {primaryProduct.avmValue > 0 ? (
                      <Money decimals value={primaryProduct.avmValue} />
                    ) : (
                      emptyValue
                    )}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Property county</TableCell>
                  <TableCell>{primaryProduct.county || emptyValue}</TableCell>
                  <TableCell />
                </TableRow>
                {!primaryProduct.decisionCriteriaName.includes("Jumbo") && (
                  <TableRow>
                    <TableCell>Conforming loan limit</TableCell>
                    <TableCell>
                      {primaryProduct.conformingLimit ? (
                        <>
                          <span style={{ verticalAlign: "middle" }}>
                            <Money decimals value={primaryProduct.conformingLimit} />{" "}
                            {primaryProduct.conformingOrHighBalance}
                          </span>
                        </>
                      ) : (
                        emptyValue
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        disabled={app.decision.decisioning_criteria_name.includes("Jumbo")}
                        onClick={() => {
                          APIClient.put(`/uw/loan_applications/${app.id}/fips`, {}, () => {
                            loadApp(dispatch, app.id);
                          });
                        }}
                        renderIcon={Renew16}
                        iconDescription="Refresh limit"
                        hasIconOnly
                      />
                      <Tooltip direction="right" tabIndex={0}>
                        <p>
                          If the property county has changed, use this button to refresh the limits
                          for this loan.
                        </p>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>Max purchase price</TableCell>
                  <TableCell>
                    <Money decimals value={primaryProduct.maxPurchasePrice} />
                    <Tooltip direction="right" tabIndex={0}>
                      <p>
                        This is the max purchase price displayed to the borrower in their dashboard,
                        and the one we send to DU pre-RESPA.
                      </p>
                    </Tooltip>
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>Max loan amount</TableCell>
                  <TableCell>
                    <Money decimals value={primaryProduct.maxLoanAmount} />
                    <Tooltip direction="right" tabIndex={0}>
                      <p>
                        This is the max loan amount displayed to the borrower in their dashboard,
                        and the one we send to DU pre-RESPA.
                      </p>
                    </Tooltip>
                  </TableCell>
                  <TableCell />
                </TableRow>
                {primaryProduct.cashOfferMaxPurchasePrice > 0 && (
                  <>
                    <TableRow>
                      <TableCell>Max Cash Offer Purchase Price</TableCell>
                      <TableCell>
                        <Money decimals value={primaryProduct.cashOfferMaxPurchasePrice} />
                        <Tooltip direction="right" tabIndex={0}>
                          <p>
                            This is the max cash offer purchase price displayed to the borrower in
                            their dashboard.
                          </p>
                        </Tooltip>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell>Max Cash Offer Loan Amount</TableCell>
                      <TableCell>
                        <Money decimals value={primaryProduct.cashOfferMaxLoanAmount} />
                        <Tooltip direction="right" tabIndex={0}>
                          <p>
                            This is the max cash offer loan amount displayed to the borrower in
                            their dashboard.
                          </p>
                        </Tooltip>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
            <Table className={styles.loanProductSummary} size="compact">
              <TableHead>
                <TableRow>
                  <TableExpandHeader />
                  <TableHeader>Monthlies</TableHeader>
                  <TableHeader>Source</TableHeader>
                  <TableHeader>Monthly values</TableHeader>
                  <TableHeader>Qualifying values</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell />
                  <TableCell style={{ paddingLeft: 0 }}>Principal &amp; interest</TableCell>
                  <TableCell />
                  <TableCell style={{ textAlign: "right" }}>
                    <Money decimals value={primaryProduct.monthlyPayment} />
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>
                    <Money decimals value={primaryProduct.qualifyingTotalPAndI} />
                  </TableCell>
                </TableRow>
                {monthlyPaymentRows("Property taxes", "propertyTax")}
                {monthlyPaymentRows("Homeowners insurance", "insurance")}
                {monthlyPaymentRows("HOA or condo dues", "hoa")}
                {monthlyPaymentRows("Supplemental Property Insurance (flood)", "floodInsurance")}
                {monthlyPaymentRows(
                  "Supplemental Property Insurance (earthquake)",
                  "earthquakeInsurance",
                )}
                {monthlyPaymentRows(
                  "Supplemental Property Insurance (wind & hail)",
                  "windAndHailInsurance",
                )}
                {monthlyPaymentRows("Mortgage insurance", "mortgageInsurance")}
                <TableRow>
                  <TableCell />
                  <TableCell style={{ paddingLeft: 0 }}>Other</TableCell>
                  <TableCell />
                  <TableCell style={{ textAlign: "right" }}>
                    <Money decimals value={primaryProduct.otherMonthlies} />
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>{emptyValue}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell style={{ paddingLeft: 0 }}>Total PITIA</TableCell>
                  <TableCell />
                  <TableCell style={{ textAlign: "right" }}>
                    <Money decimals value={primaryProduct.totalPitia} />
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>
                    <Money decimals value={primaryProduct.qualifyingTotalPitia} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell style={{ paddingLeft: 0 }}>Total Debt Including PITIA</TableCell>
                  <TableCell />
                  <TableCell style={{ textAlign: "right" }}>
                    <Money decimals value={primaryProduct.monthlyDebt} />
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>
                    <Money decimals value={primaryProduct.qualifyingMonthlyDebt} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell style={{ paddingLeft: 0 }}>DTI</TableCell>
                  <TableCell />
                  <TableCell style={{ textAlign: "right" }}>
                    {formatPercent(primaryProduct.dti * 100, 2)}
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>
                    {formatPercent(primaryProduct.dti * 100, 2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  public renderMaxEligibleLTVAtMaxPurchasePriceTitle() {
    const { calcs, worksheetOverrides, worksheet } = this.state;
    // the override is not per program, so check any
    const isOverridden =
      calcs[loanProgramKey[LoanPrograms.THIRTY_YEAR]].max_purchase_price_ltv_override !== null;
    const title = isOverridden
      ? "Max eligible LTV at max purchase price (Overridden)"
      : "Max eligible LTV at max purchase price";

    return (
      <span style={{ display: "flex", alignItems: "center" }}>
        <span>{title}</span>
        <TooltipForm
          inputType="number"
          labelText="New max ltv override"
          fieldName="operator_max_ltv"
          formValue={worksheetOverrides.operator_max_ltv}
          onChange={e => this.handleOperatorOverrideChange(e)}
          onSave={() => {
            // todo: prevent value of 0 from being sent, blows up the backend
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "operator_max_ltv",
                  value: Number(worksheetOverrides.operator_max_ltv / 100).toFixed(2),
                },
              ],
            })
              .then(() => {
                this.calculate();
              })
              .catch(err => {
                alert(JSON.stringify(err));
              });
          }}
          onClear={() => {
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "operator_max_ltv",
                  value: null,
                },
              ],
            }).then(() => {
              this.calculate();
            });
          }}
        />
      </span>
    );
  }

  public renderCalculatedReservesTitle() {
    const { calcs, worksheetOverrides, worksheet } = this.state;
    // the override is not per program, so check any
    const isOverridden =
      calcs[loanProgramKey[LoanPrograms.THIRTY_YEAR]].max_purchase_price_reserves_override !== null;
    const title = isOverridden
      ? "Calculated Required Reserves (Overridden)"
      : "Calculated Required Reserves";

    return (
      <span style={{ display: "flex", alignItems: "center" }}>
        <span>{title}</span>
        <TooltipForm
          inputType="text"
          labelText="New reserves override"
          fieldName="reserves_for_max_affordability"
          formValue={formatCurrency(worksheetOverrides.reserves_for_max_affordability, 0, 0)}
          onChange={e => this.handleOperatorOverrideChange(moneyInputToNumber(e))}
          onSave={() => {
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "reserves_for_max_affordability",
                  value: worksheetOverrides.reserves_for_max_affordability,
                },
              ],
            }).then(() => {
              this.calculate();
            });
          }}
          onClear={() => {
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "reserves_for_max_affordability",
                  value: null,
                },
              ],
            }).then(() => {
              this.calculate();
            });
          }}
        />
      </span>
    );
  }

  public renderMaxAllowableDTITitle() {
    const { calcs, worksheetOverrides, worksheet } = this.state;
    // the override is not per program, so check any
    const isOverridden = calcs[loanProgramKey[LoanPrograms.THIRTY_YEAR]].max_dti_override !== null;
    const title = isOverridden ? "Max allowable DTI (Overridden)" : "Max allowable DTI";

    return (
      <>
        <span style={{ display: "flex", alignItems: "center" }}>
          <span>{title}</span>
          <TooltipForm
            inputType="number"
            labelText="New max dti override"
            fieldName="operator_max_dti"
            formValue={worksheetOverrides.operator_max_dti}
            onChange={e => this.handleOperatorOverrideChange(e)}
            onSave={() => {
              APIClient.post(`/worksheets/${worksheet.id}`, {
                updates: [
                  {
                    name: "operator_max_dti",
                    value: Number(worksheetOverrides.operator_max_dti / 100).toFixed(4),
                  },
                ],
              })
                .then(() => {
                  this.calculate();
                })
                .catch(err => {
                  alert(JSON.stringify(err));
                });
            }}
            onClear={() => {
              APIClient.post(`/worksheets/${worksheet.id}`, {
                updates: [
                  {
                    name: "operator_max_dti",
                    value: null,
                  },
                ],
              }).then(() => {
                this.calculate();
              });
            }}
          />
        </span>
      </>
    );
  }

  public renderMaxPurchasePriceTitle() {
    const { calcs, worksheetOverrides, worksheet } = this.state;
    // the override is not per program, so check any
    // the override is not per program, so check any
    const isOverridden = !Number.isNaN(
      calcs[loanProgramKey[LoanPrograms.THIRTY_YEAR]].max_purchase_price_original,
    );

    const title = isOverridden ? "Max Purchase Price (Overridden)" : "Max Purchase Price";

    return (
      <span style={{ display: "flex", alignItems: "center" }}>
        <span>{title}</span>
        <TooltipForm
          inputType="text"
          labelText="New max purchase price override"
          fieldName="operator_max_purchase_price"
          formValue={formatCurrency(worksheetOverrides.operator_max_purchase_price, 0, 0)}
          onChange={e => this.handleOperatorOverrideChange(moneyInputToNumber(e))}
          onSave={() => {
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "operator_max_purchase_price",
                  value: worksheetOverrides.operator_max_purchase_price,
                },
              ],
            }).then(() => {
              this.calculate();
            });
          }}
          onClear={() => {
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "operator_max_purchase_price",
                  value: 0,
                },
              ],
            }).then(() => {
              this.calculate();
            });
          }}
        />
      </span>
    );
  }

  public renderMaxLoanAmountTitle() {
    const { calcs, worksheetOverrides, worksheet } = this.state;
    // the override is not per program, so check any
    // the override is not per program, so check any
    const isOverridden = !Number.isNaN(
      calcs[loanProgramKey[LoanPrograms.THIRTY_YEAR]].max_loan_amount_original,
    );

    const title = isOverridden ? "Max Loan Amount (Overridden)" : "Max Loan Amount";

    return (
      <span style={{ display: "flex", alignItems: "center" }}>
        <span>{title}</span>
        <TooltipForm
          inputType="text"
          labelText="New max loan amount override"
          fieldName="operator_max_loan_amount"
          formValue={formatCurrency(worksheetOverrides.operator_max_loan_amount, 0, 0)}
          onChange={e => this.handleOperatorOverrideChange(moneyInputToNumber(e))}
          onSave={() => {
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "operator_max_loan_amount",
                  value: worksheetOverrides.operator_max_loan_amount,
                },
              ],
            }).then(() => {
              this.calculate();
            });
          }}
          onClear={() => {
            APIClient.post(`/worksheets/${worksheet.id}`, {
              updates: [
                {
                  name: "operator_max_loan_amount",
                  value: 0,
                },
              ],
            }).then(() => {
              this.calculate();
            });
          }}
        />
      </span>
    );
  }

  public renderMaxPurchasePrice(program: string) {
    const { calcs } = this.state;
    return (
      <>
        {!Number.isNaN(calcs[program].max_purchase_price_original) ? (
          <>
            <span className={styles.overriddenValue}>
              <Money value={calcs[program].max_purchase_price_original} />
            </span>{" "}
            <Money value={calcs[program].max_purchase_price} />
          </>
        ) : (
          <Money value={calcs[program].max_purchase_price} />
        )}
      </>
    );
  }

  public renderMaxLoanAmount(program: string) {
    const { calcs } = this.state;
    return (
      <>
        {!Number.isNaN(calcs[program].max_loan_amount_original) ? (
          <>
            <span className={styles.overriddenValue}>
              <Money value={calcs[program].max_loan_amount_original} />
            </span>{" "}
            <Money value={calcs[program].max_loan_amount} />
          </>
        ) : (
          <Money value={calcs[program].max_loan_amount} />
        )}
      </>
    );
  }

  public renderMaxEligibleLTVAtMaxPurchasePrice(program: string) {
    const { calcs } = this.state;
    const override = calcs[program].max_purchase_price_ltv_override;
    const calculated = calcs[program].max_purchase_price_ltv_calculated;

    if (override !== null) {
      return (
        <>
          <span className={styles.overriddenValue}>{formatPercent(calculated * 100, 2)}</span>
          <span style={{ marginLeft: 5 }}>{formatPercent(override * 100, 2)}</span>
        </>
      );
    }

    return <span>{formatPercent(calculated * 100, 2)}</span>;
  }

  public renderMaxLTVBucket(program: string) {
    const { calcs } = this.state;
    const value = calcs[program].max_bucket_ltv;

    return <>{formatPercent(value * 100, 2)} </>;
  }

  public renderCalculatedReserves(program: string) {
    const { calcs } = this.state;
    const override = calcs[program].max_purchase_price_reserves_override;
    const calculated = calcs[program].max_purchase_price_reserves_calculated;

    if (override !== null) {
      return (
        <>
          <span className={styles.overriddenValue}>
            <Money value={calculated} />
          </span>
          <span style={{ marginLeft: 5 }}>
            <Money value={override} />
          </span>
        </>
      );
    }

    return <Money value={calculated} />;
  }

  public renderMaxAllowableDTI(program: string) {
    const { calcs } = this.state;
    const override = calcs[program].max_dti_override;
    const calculated = calcs[program].max_dti_calculated;
    if (override) {
      return (
        <>
          <span className={styles.overriddenValue}>{formatPercent(calculated * 100, 2)}</span>
          <span style={{ marginLeft: 5 }}>{formatPercent(override * 100, 2)}</span>
        </>
      );
    }

    return <span>{formatPercent(calculated * 100, 2)}</span>;
  }

  public fetchWorksheets = () => {
    const { app } = this.props;
    return APIClient.get(`/worksheets/${app.loan_file_worksheet_id}`)
      .then((resp: any) => deepInstanceToWsList(app.id, WS_NAME.LOAN_FILE, resp.worksheet))
      .then(worksheets => {
        const defaultsWS = getWsByName(worksheets, WS_NAME.LOAN_FILE_DEFAULTS);
        this.setState({
          worksheetOverrides: {
            decision_criteria: defaultsWS.instance.decision_criteria,
            operator_closing_cost_estimated_percent: defaultsWS.instance
              .operator_closing_cost_estimated_percent
              ? Number(defaultsWS.instance.operator_closing_cost_estimated_percent) * 100
              : 0,
            operator_max_dti: defaultsWS.instance.operator_max_dti
              ? Number(defaultsWS.instance.operator_max_dti) * 100
              : 0,
            operator_max_loan_amount: defaultsWS.instance.operator_max_loan_amount
              ? Number(defaultsWS.instance.operator_max_loan_amount)
              : 0,
            operator_max_purchase_price: defaultsWS.instance.operator_max_purchase_price
              ? Number(defaultsWS.instance.operator_max_purchase_price)
              : 0,
            operator_max_ltv: defaultsWS.instance.operator_max_ltv
              ? Number(defaultsWS.instance.operator_max_ltv) * 100
              : 0,
            operator_minimum_loan_amount: defaultsWS.instance.operator_minimum_loan_amount
              ? Number(defaultsWS.instance.operator_minimum_loan_amount)
              : 0,
            reserves_for_max_affordability: defaultsWS.instance.reserves_for_max_affordability
              ? Number(defaultsWS.instance.reserves_for_max_affordability)
              : 0,
          },
          worksheet: defaultsWS,
        });
      });
  };

  public renderTable() {
    const { app } = this.props;
    const { decision } = app;
    const { existing_monthly_debt, monthly_income } = decision;
    const {
      calcs,
      decision_criteria,
      purchase_price,
      loan_amount_base,
      down_payment,
      down_payment_percent,
      expandedRows,
    } = this.state;
    const applicableProducts = this.applicableProducts();

    // check if data for all products has loaded; otherwise, bail out
    for (let i = 0, l = applicableProducts.length; i < l; i++) {
      const p = applicableProducts[i];
      if (!calcs[loanProgramKey[p]]) {
        return <h3>Loading calculator...</h3>;
      }
    }

    const existingMonthlyDebt = parseFloat(existing_monthly_debt);
    const monthlyIncome = parseFloat(monthly_income);
    const activeDCs = decisionCriteriaOptions(decision_criteria);

    return (
      <div className="no-print">
        <h3 style={{ marginBottom: 15 }}>Sandbox Calculator</h3>
        <Tooltip direction="right" tabIndex={0} triggerText="What is this?">
          <p>
            This is just a calculator. You can change these values and they are not persisted to the
            loan file. Changing the inputs here will (might) change the outputs below.{" "}
          </p>
        </Tooltip>
        <FormGroup className={styles.calculatorForm} legendText="">
          <ComboBox
            titleText="Decision criteria"
            id="decision_criteria"
            name="decision_criteria"
            placeholder="decision criteria"
            selectedItem={activeDCs.find(x => x.value === decision_criteria)}
            type="default"
            onChange={e => {
              this.handleComboBoxChange("decision_criteria", e);
            }}
            size="sm"
            items={activeDCs}
          />
          <div style={{ marginBottom: "1rem" }} />
          <TextInput
            labelText="Purchase price"
            id="purchase_price"
            name="purchase_price"
            value={formatCurrency(purchase_price, 0, 2)}
            type="text"
            onChange={e => this.handleSandboxCalculatorChange(moneyInputToNumber(e))}
            onBlur={e => this.handleSandboxCalculatorChange(moneyInputToNumber(e))}
            size="sm"
          />
          <div style={{ marginBottom: "1rem" }} />
          <TextInput
            labelText="Loan amount"
            id="loan_amount_base"
            name="loan_amount_base"
            value={formatCurrency(loan_amount_base, 0, 2)}
            type="text"
            onChange={e => this.handleSandboxCalculatorChange(moneyInputToNumber(e))}
            onBlur={e => this.handleSandboxCalculatorChange(moneyInputToNumber(e))}
            size="sm"
          />
          <div style={{ marginBottom: "1rem" }} />
          <TextInput
            labelText="Downpayment"
            id="down_payment"
            name="down_payment"
            value={formatCurrency(down_payment, 0, 2)}
            type="text"
            onChange={e => this.handleSandboxCalculatorChange(moneyInputToNumber(e))}
            onBlur={e => this.handleSandboxCalculatorChange(moneyInputToNumber(e))}
            size="sm"
          />
          <div style={{ marginBottom: "1rem" }} />
          <TextInput
            labelText="Downpayment %"
            id="down_payment_percent"
            name="down_payment_percent"
            value={down_payment_percent}
            type="text"
            onChange={e => this.handleSandboxCalculatorChange(e)}
            onBlur={e => this.handleSandboxCalculatorChange(e)}
            size="sm"
          />
          <div style={{ marginBottom: "1rem" }} />
        </FormGroup>
        <Table className={styles.loanProductMaximums} size="compact">
          <TableBody>
            <TableRow style={{ fontWeight: "bold" }}>
              <TableExpandHeader />
              <TableHeader>
                <h4 style={{ fontWeight: "bold" }}>Requested</h4>
              </TableHeader>
              {applicableProducts.map(p => (
                <TableHeader key={p}>{operatorHumanizeLoanProgramName[p]}</TableHeader>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Input loan amount</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].loan_amount_base} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Input purchase price</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].input_purchase_price} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Input downpayment</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].input_down_payment} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Interest rate</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{calcs[loanProgramKey[p]].rate}%</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>APR</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{calcs[loanProgramKey[p]].apr}%</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Monthly P&amp;I</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].monthly_payment} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Monthly PITIA</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].combined_monthly_payment} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Total monthly debt with subject PITIA</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money
                    value={calcs[loanProgramKey[p]].combined_monthly_payment + existingMonthlyDebt}
                  />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Qualifying interest rate</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{calcs[loanProgramKey[p]].qualifying_rate}%</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Qualifying monthly P&amp;I</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].qualifying_monthly_payment} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Qualifying monthly PITIA</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].qualifying_combined_monthly_payment} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Qualifying total monthly debt with subject PITIA</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money
                    value={
                      calcs[loanProgramKey[p]].qualifying_combined_monthly_payment +
                      existingMonthlyDebt
                    }
                  />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>DTI for requested loan amount</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  {(
                    ((calcs[loanProgramKey[p]].qualifying_combined_monthly_payment +
                      existingMonthlyDebt) /
                      monthlyIncome) *
                    100
                  ).toFixed(1)}
                  %
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>LTV for requested loan amount and purchase price</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  {((100 * Number(loan_amount_base)) / Number(purchase_price)).toFixed(2)}%
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Max LTV for input purchase price</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  {(calcs[loanProgramKey[p]].max_ltv_for_input_purchase_price * 100).toFixed(2)}%
                </TableCell>
              ))}
            </TableRow>
            <TableRow style={{ fontWeight: "bold" }}>
              <TableExpandHeader />
              <TableHeader>
                <h4 style={{ fontWeight: "bold" }}>Mortgage Maximums</h4>
              </TableHeader>
              {applicableProducts.map(p => (
                <TableHeader key={p}>{operatorHumanizeLoanProgramName[p]}</TableHeader>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>{this.renderMaxPurchasePriceTitle()}</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{this.renderMaxPurchasePrice(loanProgramKey[p])}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>{this.renderMaxLoanAmountTitle()}</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{this.renderMaxLoanAmount(loanProgramKey[p])}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Max downpayment</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].down_payment_at_max} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>LTV at max purchase price & loan amount</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{calcs[loanProgramKey[p]].ltv_at_max.toFixed(2)}%</TableCell>
              ))}
            </TableRow>
            <TableExpandRow
              onExpand={() => {
                this.setState(prevState => {
                  return {
                    ...prevState,
                    expandedRows: {
                      ...prevState.expandedRows,
                      qualifyingMonthlyPitia: !prevState.expandedRows.qualifyingMonthlyPitia,
                    },
                  };
                });
              }}
              isExpanded={expandedRows.qualifyingMonthlyPitia}
              ariaLabel="Expand row to see details"
            >
              <TableCell>Qualifying monthly PITIA at Max</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].max_qualifying_combined_monthly_payment} />
                </TableCell>
              ))}
            </TableExpandRow>
            {expandedRows.qualifyingMonthlyPitia && (
              <TableExpandedRow colSpan={5}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Monthly</TableHeader>
                      {applicableProducts.map(p => (
                        <TableHeader key={p}>{p}</TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>P&I</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={p}>
                          <Money value={calcs[loanProgramKey[p]].max_monthly_payment} />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>Property Tax</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={`${p}_property_taxes`}>
                          <Money value={app.decision.monthly_property_taxes[0].amount} />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>Homeowner's Insurance</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={`${p}_monthly_home_owners_insurance`}>
                          <Money value={app.decision.monthly_home_owners_insurance[0].amount} />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>HOA Dues</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={`${p}_monthly_home_owners_association`}>
                          <Money value={app.decision.monthly_home_owners_association[0].amount} />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>Flood Insurance</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={`${p}_monthly_flood_insurance`}>
                          <Money value={app.decision.monthly_flood_insurance[0].amount} />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>Earthquake Insurance</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={`${p}_monthly_earthquake_insurance`}>
                          <Money value={app.decision.monthly_earthquake_insurance[0].amount} />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>Wind & Hail Insurance</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={`${p}_monthly_wind_and_hail_insurance`}>
                          <Money value={app.decision.monthly_wind_and_hail_insurance[0].amount} />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>Mortgage Insurance</TableCell>
                      {applicableProducts.map(p => (
                        <TableCell key={`${p}_monthly_mortgage_insurance`}>
                          <Money value={app.decision.monthly_mortgage_insurance[0].amount} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableExpandedRow>
            )}
            <TableRow>
              <TableCell />
              <TableCell>DTI at Max</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  {(
                    ((calcs[loanProgramKey[p]].max_qualifying_combined_monthly_payment +
                      existingMonthlyDebt) /
                      monthlyIncome) *
                    100
                  ).toFixed(1)}
                  %
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>{this.renderMaxAllowableDTITitle()}</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{this.renderMaxAllowableDTI(loanProgramKey[p])}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>{this.renderMaxEligibleLTVAtMaxPurchasePriceTitle()}</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  {this.renderMaxEligibleLTVAtMaxPurchasePrice(loanProgramKey[p])}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Max LTV</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{this.renderMaxLTVBucket(loanProgramKey[p])}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>{this.renderCalculatedReservesTitle()}</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{this.renderCalculatedReserves(loanProgramKey[p])}</TableCell>
              ))}
            </TableRow>
            <TableRow style={{ fontWeight: "bold" }}>
              <TableExpandHeader />
              <TableHeader>
                <h4 style={{ fontWeight: "bold" }}>Cash Offer Affordability</h4>
              </TableHeader>
              {applicableProducts.map(p => (
                <TableHeader key={p}>{operatorHumanizeLoanProgramName[p]}</TableHeader>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Max Cash Offer purchase price</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].max_cash_close_purchase_price} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Max Cash Offer loan amount</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].max_cash_close_loan_amount} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Max LTV Bucket</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>{this.renderMaxLTVBucket(loanProgramKey[p])}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Min Appraisal for max Cash Offer loan amount</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money value={calcs[loanProgramKey[p]].min_appraisal_for_max_cc_loan_amount} />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Appraisal wiggle room @ max</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money
                    value={
                      calcs[loanProgramKey[p]].max_cash_close_purchase_price -
                      calcs[loanProgramKey[p]].min_appraisal_for_max_cc_loan_amount
                    }
                  />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>
                <span>Min Appraisal for </span>
                <Money
                  value={calcs[loanProgramKey[applicableProducts[0]]].input_cc_purchase_price}
                />
                <span> purchase price</span>
              </TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money
                    value={calcs[loanProgramKey[p]].min_appraisal_for_input_cc_purchase_price}
                  />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>Appraisal Wiggle Room funds</TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <Money
                    value={
                      calcs[loanProgramKey[p]].input_cc_purchase_price -
                      calcs[loanProgramKey[p]].min_appraisal_for_input_cc_purchase_price
                    }
                  />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>
                <span>Appraisal Wiggle Room as % of </span>
                <Money
                  value={calcs[loanProgramKey[applicableProducts[0]]].input_cc_purchase_price}
                />
              </TableCell>
              {applicableProducts.map(p => (
                <TableCell key={p}>
                  <span>
                    {formatPercent(
                      (100 *
                        (calcs[loanProgramKey[p]].input_cc_purchase_price -
                          calcs[loanProgramKey[p]].min_appraisal_for_input_cc_purchase_price)) /
                        calcs[loanProgramKey[p]].input_cc_purchase_price,
                      2,
                    )}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  public render() {
    const { app, onHide, hurdle } = this.props;

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        {this.renderSummary()}
        {this.renderTable()}
      </HurdleView>
    );
  }
}

export default connect()(LoanProductHurdleEditor);
